// Stock in the Floor's "What needs you" (spec/13 §3.2, §6.4; `inventory:read`, where stock is on): one line that sums
// it up — "3 low · 1 needs a recount · 2 sold out" — opening the Stock list on what needs someone, then the items
// themselves, worst first (below zero, out, low), each opening that item, and the dishes stock has sold out, opening
// the menu. Drawn with the panel's own sections and rows, like its other lines.

import type { StockAlertItem, StockAlerts } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Icon } from '../../shell/nav-icons';
import { useStockFormat, useStockUnits } from './format';
import { useStockAlerts } from './queries';

import './stock.css';

/** How many items the section lists before "and N more" (the list holds the rest). */
const SHOWN = 5;

/** The alerts while stock is on and the member may read them; nothing otherwise. Its count joins the panel's. */
export function useStockNeeds(organizationId: string, venueId: string, enabled: boolean): { readonly alerts: StockAlerts | undefined; readonly count: number } {
  const alerts = useStockAlerts(organizationId, venueId, enabled);
  const data = enabled && alerts.data?.enabled === true ? alerts.data : undefined;
  const count = data === undefined ? 0 : data.negative.length + data.out.length + data.low.length + data.soldOut.length;
  return { alerts: data, count };
}

export function StockNeedsSection({ alerts, venueSlug }: { readonly alerts: StockAlerts | undefined; readonly venueSlug: string }): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  // An alert names only the base unit; what a piece is called ("cans") comes from the stock list, read only if needed.
  const units = useStockUnits(alerts !== undefined && [...alerts.low, ...alerts.out, ...alerts.negative].some((item) => item.baseUnit === 'each'));
  if (alerts === undefined) return null;
  const items: readonly StockAlertItem[] = [...alerts.negative, ...alerts.out, ...alerts.low];
  const total = items.length + alerts.soldOut.length;
  if (total === 0) return null;
  const summary = [
    alerts.low.length > 0 ? t('floor.needsYou.stockLow', { count: alerts.low.length }) : null,
    alerts.out.length > 0 ? t('floor.needsYou.stockOut', { count: alerts.out.length }) : null,
    alerts.negative.length > 0 ? t('floor.needsYou.stockRecount', { count: alerts.negative.length }) : null,
    alerts.soldOut.length > 0 ? t('floor.needsYou.stockSoldOut', { count: alerts.soldOut.length }) : null,
  ].filter((part): part is string => part !== null);
  const state = (item: StockAlertItem) =>
    item.level === 'low' && item.parLevel !== null
      ? t('floor.needsYou.stockLowState', { onHand: stock.quantity(item.onHand, units(item.stockItemId, item.baseUnit)), par: stock.quantity(item.parLevel, units(item.stockItemId, item.baseUnit)) })
      : item.level === 'negative'
        ? t('floor.needsYou.stockNegativeState', { onHand: stock.quantity(item.onHand, units(item.stockItemId, item.baseUnit)) })
        : t(`stock.level.${item.level}`);

  return (
    <section className="console-floor__panel-section" aria-labelledby="console-floor-stock">
      <h3 className="console-floor__panel-subtitle" id="console-floor-stock">
        <span>{t('floor.needsYou.stock')}</span>
        <Badge tone={alerts.negative.length > 0 || alerts.out.length > 0 ? 'danger' : 'warn'}>{total}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        <li>
          <Link to="/v/$venueSlug/stock/$tab" params={{ venueSlug, tab: 'items' }} search={{ level: 'attention' }} className="console-floor__panel-item">
            <span className="console-floor__panel-name console-stock-needs__summary">{summary.join(' · ')}</span>
            <span className="console-floor__panel-go" aria-hidden="true">
              <Icon name="chevronRight" />
            </span>
          </Link>
        </li>
        {items.slice(0, SHOWN).map((item) => (
          <li key={item.stockItemId}>
            <Link to="/v/$venueSlug/stock/$tab" params={{ venueSlug, tab: 'items' }} search={{ item: item.stockItemId }} className="console-floor__panel-item">
              <span className="console-floor__panel-name">{item.name}</span>
              <span className={item.level === 'low' ? 'console-floor__panel-state console-num' : 'console-floor__panel-state console-num console-stock-alert--danger'}>{state(item)}</span>
              <span className="console-floor__panel-go" aria-hidden="true">
                <Icon name="chevronRight" />
              </span>
            </Link>
          </li>
        ))}
        {items.length > SHOWN ? (
          <li>
            <Link to="/v/$venueSlug/stock/$tab" params={{ venueSlug, tab: 'items' }} search={{ level: 'attention' }} className="console-floor__panel-item">
              <span className="console-floor__panel-name console-muted">{t('floor.needsYou.stockMore', { count: items.length - SHOWN })}</span>
            </Link>
          </li>
        ) : null}
        {alerts.soldOut.length > 0 ? (
          <li>
            <Link to="/v/$venueSlug/menu" params={{ venueSlug }} className="console-floor__panel-item">
              <span className="console-floor__panel-name">{t('floor.needsYou.stockSoldOutItem', { names: alerts.soldOut.map((product) => product.name).join(', ') })}</span>
              <span className="console-floor__panel-go" aria-hidden="true">
                <Icon name="chevronRight" />
              </span>
            </Link>
          </li>
        ) : null}
      </ul>
    </section>
  );
}
